import _ from 'lodash';
import { useState } from 'react';
import { Field, useForm } from 'react-final-form';
import { useEffectOnceWhen } from 'rooks';
import { CKEditorAsync } from '@/annotation/CKEditorAsync.organism';
import { getCKEditorInstance } from '@/annotation/ckEditor.utilities';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { getFormFieldWrapperProps, getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import type { CkEditorInstanceId } from '@/annotation/ckEditor.utilities';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';

export interface CKEditorFormComponentProps extends ValidatingFormComponent<string> {
  component: 'CKEditorFormComponent';
  placeholder?: string;
  id?: string;
  autoFocus?: boolean;
  displayError?: boolean;
  labelIcon?: string;
  labelIconAction?: () => void;
  labelIconTooltip?: string;
  extraClassNames?: string;
  labelClassNames?: string;
  wrapperClassNames?: string;
  forceUpdateText?: string;
  overlay?: React.ReactNode;
  useMarkdown?: boolean;
  canEdit?: boolean;
}

const toolbar = [
  'heading',
  // 'imageInsert',
  '|',
  'fontFamily',
  'fontSize',
  'fontColor',
  'fontBackgroundColor',
  '|',
  'bold',
  'italic',
  'underline',
  '|',
  'alignment',
  'outdent',
  'indent',
  'numberedList',
  'bulletedList',
  '|',
  'link',
];

export const CKEditorFormComponent = (props: CKEditorFormComponentProps) => {
  const {
    value,
    name,
    placeholder,
    extendValidation,
    validation,
    onChange,
    testId = 'formControl',
    required,
    customErrorText,
    labelIcon,
    labelIconAction,
    labelIconTooltip,
    extraClassNames,
    labelClassNames,
    wrapperClassNames,
    forceUpdateText,
    overlay,
    useMarkdown,
    canEdit = true,
    autoFocus = false,
  } = props;

  const defaultValidation = (value: string) => !!(required && _.isEmpty(_.trim(value)));
  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);
  const formState = useForm().getState();
  const [ckKReady, setCKReady] = useState(false);

  const domId: CkEditorInstanceId = `formComponentEditor-${testId}`;

  useEffectOnceWhen(() => {
    value && getCKEditorInstance(domId)?.setData(value);
  }, ckKReady);

  const showError =
    _.has(formState.errors, name) &&
    formState.hasValidationErrors &&
    ((formState.modified && formState.modified[name]) || formState.submitFailed);

  const onDocumentChange = () => {
    const editor = getCKEditorInstance(domId);
    if (editor) {
      onChange?.(editor.getData());
    }
  };

  return (
    <FormFieldWrapper
      {...getFormFieldWrapperProps(props, ['id'])}
      testId={`${testId}-wrap`}
      labelIcon={labelIcon}
      labelIconAction={labelIconAction}
      labelIconTooltip={labelIconTooltip}
      labelClassNames={labelClassNames}
      wrapperClassNames={wrapperClassNames}
      extraClassNames={`ckEditorFormComponent${showError ? ' error' : ''} ${extraClassNames}`}
      showError={showError}
      customErrorText={customErrorText}>
      <Field name={name} validate={appliedValidation}>
        {({ input, meta }) => {
          return (
            <div className="flexFill height-maximum flexRowContainer tw:relative">
              <CKEditorAsync
                forceUpdateText={forceUpdateText}
                id={`CKEditor${testId}`}
                placeholder={placeholder}
                document={value}
                toolbar={useMarkdown ? undefined : toolbar} // simplified toolbar already used with markdown
                plugins={[]}
                canEdit={canEdit}
                afterOnInit={() => setCKReady(true)}
                onDestroy={() => undefined}
                onDocumentChange={onDocumentChange}
                domId={domId}
                shouldFocusOnInit={autoFocus}
                useMarkdown={useMarkdown}
              />
              {overlay && (
                <div className="flexRowContainer flexFill height-maximum width-maximum tw:absolute tw:mt-sq-15 tw:items-center tw:justify-center">
                  {overlay}
                </div>
              )}
            </div>
          );
        }}
      </Field>
    </FormFieldWrapper>
  );
};
